import { CalendarDays, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';
import { describeDate, todayISO } from '@/shared/lib/dates';
import { springs } from '@/shared/lib/motion';
import { t } from '@/shared/i18n';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/Popover';

import styles from './DatePicker.module.scss';

export interface DatePickerProps {
  /** YYYY-MM-DD или пустая строка. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Как показать выбранное; по умолчанию — дата словами. */
  children?: ReactNode;
  /** Подпись пустого значения. */
  placeholder?: string;
  /** Доступное имя кнопки, когда смысл даты задаёт контекст. */
  ariaLabel?: string;
  className?: string;
  /** Без рамки — как текст в строке списка; рамка только на ховере. */
  quiet?: boolean;
  /** Без иконки календаря — когда смысл поля уже подписан рядом. */
  hideIcon?: boolean;
  /** Крестик «очистить»; у обязательной даты его быть не должно. */
  clearable?: boolean;
}

const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

function iso(d: Date): string {
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parse(value: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return null;
  }

  const [y, m, d] = value.split('-').map(Number);

  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
}

function shift(base: Date, days: number): string {
  const d = new Date(base);

  d.setDate(d.getDate() + days);

  return iso(d);
}

/**
 * Календарь в поповере: свой, а не нативный `input type=date` — тот в
 * разных браузерах выглядит по-разному, а в некоторых не открывается
 * вовсе. Быстрые варианты сверху (сегодня, завтра, через неделю),
 * месяц листается стрелками, смена месяца — пружиной.
 */
export function DatePicker({
  value,
  onChange,
  disabled = false,
  children,
  placeholder,
  ariaLabel,
  className,
  quiet = false,
  hideIcon = false,
  clearable = true,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const selected = parse(value);
  const today = parse(todayISO()) ?? new Date();
  const [cursor, setCursor] = useState(() => selected ?? today);
  const [direction, setDirection] = useState(0);

  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  const move = (months: number) => {
    setDirection(months);
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + months, 1));
  };

  // Сетка: с понедельника, шесть недель — высота не прыгает между месяцами.
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first);

  start.setDate(1 - offset);

  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);

    d.setDate(start.getDate() + i);

    return d;
  });

  const monthLabel = cursor.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  const quick = [
    { label: t.date.today, value: iso(today) },
    { label: t.date.tomorrow, value: shift(today, 1) },
    { label: t.date.nextWeek, value: shift(today, 7) },
  ];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);

        if (next) {
          setCursor(selected ?? today);
          setDirection(0);
        }
      }}
    >
      <PopoverTrigger asChild disabled={disabled}>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cx(
            styles['trigger'],
            value === '' && styles['trigger--empty'],
            quiet && styles['trigger--quiet'],
            className,
          )}
          disabled={disabled}
        >
          {!hideIcon && <CalendarDays />}
          <span className={styles['trigger__text']}>
            {children ?? (value === '' ? placeholder : describeDate(value))}
          </span>
          {value !== '' && !disabled && clearable && (
            <span
              role="button"
              tabIndex={0}
              className={styles['trigger__clear']}
              aria-label={t.date.clear}
              onClick={(event) => {
                event.stopPropagation();
                onChange('');
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  event.stopPropagation();
                  onChange('');
                }
              }}
            >
              <X />
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" className={styles['popover']}>
        <div className={styles['quick']}>
          {quick.map((q) => (
            <button
              key={q.value}
              type="button"
              className={cx(
                styles['quick__item'],
                value === q.value && styles['quick__item--active'],
              )}
              onClick={() => {
                pick(q.value);
              }}
            >
              {q.label}
            </button>
          ))}
        </div>

        <div className={styles['head']}>
          <button
            type="button"
            className={styles['nav']}
            aria-label={t.date.prevMonth}
            onClick={() => {
              move(-1);
            }}
          >
            <ChevronLeft />
          </button>
          <span className={styles['month']}>{monthLabel}</span>
          <button
            type="button"
            className={styles['nav']}
            aria-label={t.date.nextMonth}
            onClick={() => {
              move(1);
            }}
          >
            <ChevronRight />
          </button>
        </div>

        <div className={styles['weekdays']}>
          {WEEKDAYS.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>

        <div className={styles['grid-clip']}>
          <AnimatePresence initial={false} mode="popLayout" custom={direction}>
            <motion.div
              key={`${String(cursor.getFullYear())}-${String(cursor.getMonth())}`}
              className={styles['grid']}
              custom={direction}
              initial={{ x: direction * 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction * -40, opacity: 0 }}
              transition={springs.rubber}
            >
              {cells.map((d) => {
                const v = iso(d);
                const outside = d.getMonth() !== cursor.getMonth();

                return (
                  <button
                    key={v}
                    type="button"
                    className={cx(
                      styles['day'],
                      outside && styles['day--outside'],
                      v === iso(today) && styles['day--today'],
                      v === value && styles['day--selected'],
                    )}
                    onClick={() => {
                      pick(v);
                    }}
                  >
                    {d.getDate()}
                  </button>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </PopoverContent>
    </Popover>
  );
}
